/* SPEC-147 — pure card-placement geometry, extracted from TourCard so the
 * "card is ALWAYS fully within the viewport" invariant can be asserted without a
 * DOM (see scripts/verify-tours.mjs). TourCard passes the live window size; the
 * math here is a pure function of (anchor rect, card height, viewport), which is
 * why it is testable in cheap Node rather than only by eye. */

export const CARD_WIDTH = 360;
export const GAP = 14;
export const MARGIN = 12;

/** Only the anchor edges the placement needs — a DOMRect satisfies this. */
export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface CardBox {
  top: number;
  left: number;
  width: number;
}

/**
 * Place the narration card near the anchor, guaranteeing it sits fully on-screen.
 * Prefers the side of the anchor with more room (below → above → whichever side
 * has more space) and then hard-clamps `top` into [MARGIN, vh - cardHeight - MARGIN]
 * so the card never extends past a viewport edge. On a short viewport the card may
 * overlap the dimmed area, but stays fully readable — anchor visibility (scrolled
 * to centre by the provider) plus a readable card is the contract.
 */
export function computeCardPosition(
  rect: AnchorRect | null,
  cardHeight: number,
  vp: Viewport,
): CardBox {
  const { width: vw, height: vh } = vp;
  const width = Math.min(CARD_WIDTH, vw - MARGIN * 2);
  // The hard clamp range for `top`. When the card is taller than the viewport the
  // range collapses to MARGIN and it can only pin to the top (overflow unavoidable).
  const maxTop = Math.max(MARGIN, vh - cardHeight - MARGIN);

  // No target → centre the card (still clamped so a tall card can't overflow).
  if (!rect) {
    return {
      top: Math.min(Math.max(MARGIN, (vh - cardHeight) / 2), maxTop),
      left: Math.max(MARGIN, (vw - width) / 2),
      width,
    };
  }

  const spaceBelow = vh - rect.bottom - GAP;
  const spaceAbove = rect.top - GAP;
  let top: number;
  if (cardHeight + MARGIN <= spaceBelow) {
    top = rect.bottom + GAP;
  } else if (cardHeight + MARGIN <= spaceAbove) {
    top = rect.top - GAP - cardHeight;
  } else {
    top = spaceBelow >= spaceAbove ? maxTop : MARGIN;
  }
  top = Math.min(Math.max(top, MARGIN), maxTop);

  // Horizontally, align to the anchor's left edge, clamped on-screen.
  let left = rect.left;
  if (left + width + MARGIN > vw) left = vw - width - MARGIN;
  if (left < MARGIN) left = MARGIN;

  return { top, left, width };
}
